import { Component, ElementRef, Injector, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSidenav } from '@angular/material/sidenav';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { FormStatusEnum, FormType } from 'src/app/common/enumerations/enumerations';
import { FormsStoreAndListenersService } from 'src/app/controls/form-table/store/forms.store';
import { Form, FormHandler } from 'src/app/services/forms/form';
import { EbSideComponent } from './eb-side/eb-side.component';
import moment from 'moment';
import { MessagePlaceholder } from 'src/app/common/models/placeholder';
import { utils } from 'src/app/modules/libs/utils';
import { EbService } from './eb.service';
import { FormService } from 'src/app/controls/form-table/form.service';
import { SequenceNumber } from 'src/app/services/forms/sequence-number';
import { FormVersion } from 'src/app/services/forms/form-version';
import { EB2 } from './eb';
import { EbMainComponent } from './eb-main/eb-main.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { FormVersionService } from 'src/app/services/forms/form-version.service';
import { CanComponentDeactivate } from 'src/app/common/guards/unsaved-changes.guard';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { FormHistoryButtonComponent } from 'src/app/controls/form-history/form-history-button/form-history-button.component';
import { FormsUpdate } from 'src/app/controls/form-table/store/forms.action';

@Component({
  selector: 'app-eb',
  templateUrl: './eb.component.html',
  styleUrls: ['./eb.component.scss']
})
export class EbComponent extends BaseComponent implements OnChanges, CanComponentDeactivate, OnDestroy {


  @ViewChild("sidenav") sidenav!: MatSidenav;
  @ViewChild("headerTop") headerTop!: ElementRef;
  @ViewChild(EbSideComponent) side!: EbSideComponent;
  @ViewChild(EbMainComponent) main!: EbMainComponent;
  @ViewChild(FormHistoryButtonComponent) public showHistory!: FormHistoryButtonComponent;
  disabled: boolean = false;

  loading = false;

  resourceRequired: boolean = false;

  filterCtrl = new FormControl();

  errorMessages: string[] = [];
  headerOnTop: boolean = false;

  valueFilter!: string;
  createdOn!: string;

  ///////
  tmpFilter!: string;
  condition!: string;
  filteredFilter: any[] = [];
  currentFilter: any;
  filter: any;

  formUnchanged?: Form | null;
  form?: Form | null;
  forms: Form[] = [];
  forms$!: Observable<Form[]>;
  formsSubs: Subscription = new Subscription;

  loadingMessage!: string;

  showAmendmentToggle: boolean = false;
  showAmendment: boolean = false;
  private readonly amendmentSubmitCode = 'EB-AMENDMENT-SUBMIT';
  //////

  buttonsVisible: boolean | null = false;

  constructor(
    public service: EbService,
    private formService: FormService,
    private formVersionService: FormVersionService,
    private formStore: FormsStoreAndListenersService,
    protected override injector: Injector
  ) {
    super(injector);
    this.formStore.initStore(FormType.EBF);
  }

  ngAfterViewInit(): void {
    this.setHeights();
  }

  ngOnChanges(changes: SimpleChanges): void {

  }

  ngOnInit() {
    this.moduleTitle = 'Equipment Bypass Forms'
    this.loadForms();
  }

  loadForms() {
    this.forms$ = this.store.select(state => state.Forms.data);
    this.formsSubs = this.forms$.subscribe(data => {
      if (data?.length) {
        this.forms = data;
        if (this.form) {
          const form = this.forms.find(f => f.id == this.form?.id);
          if (form) {
            this.form = form;
            this.rowSelected(this.form);
          }
        }
      }
    });
  }

  showStatusChangeMessage(form: Form) {
    this.alert.message('formStateChanged', [
      new MessagePlaceholder("{serialNo}", form.sequenceNumber.number), new MessagePlaceholder("{status}", form.formVersion.status.name ?? 'New'),]);
  }

  override ngOnDestroy(): void {
    this.cancelSerialNumbers();
    this.formsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  clear() {
    this.errorMessages = [];
  }

  async create() {
    if (await this.canDeactivate()) {
      const user = this.getCurrentUser();
      const serialNo = await this.formService.getSerialNo(FormType.EBF).toPromise();
      if (serialNo && user) {
        this.form = {
          id: -1,
          type: FormType.EBF,
          sequenceNumber: new SequenceNumber(),
          formVersion: new FormVersion(FormType.EBF),
          editingById: user.id,
          editingBy: user,
          createdByUser: user,
          createdOn: new Date(),
          date: new Date()
        } as Form;
        this.form.sequenceNumber.number = serialNo?.serialNo;
        this.form.formVersion.statusID == FormStatusEnum.New;
        this.form.formVersion.operatorInitiatingForm = user;
        this.form.formVersion.operatorInitiatingFormID = user.id ?? 0;
        this.form.formVersion.eB2 = new EB2;
        this.showAmendment = false;
        this.side.unselect();
        this.setOpenDocument(this.formTypeEnum.EBF, -1);
        this.setFormDirty();
        this.disabled = false;
      }
    }
  }

  async cancelSerialNumbers() {
    if (this.form?.id == 0) {
      await this.formService.deleteSerialNumbers(FormType.EBF, this.currentUser?.id as number).toPromise();
    }
  }

  async edit(value: boolean) {
    if (this.form?.id) {
      await this.formService.setEditing(this.form?.id as number, value).toPromise();
    }
    else if (await this.canDeactivate()) {
      await this.cancelSerialNumbers();
    }
    if (!value) {
      this.form = null;
      this.setOpenDocument(FormType.EBF);
      this.side.formTable.form = null;
    }
  }

  /**
   * Loads the selected EB and opens its pending amendment view when approval is awaiting action.
   * @param e The selected form row, or null when the selection is cleared.
   */
  rowSelected(e: Form | any) {
    // Selecting a form must return to the live form view; otherwise eb-main is removed
    // by the history toggle and its amendment actions are never evaluated.
    if (this.showHistory) this.showHistory.value = false;
    if (!e) {
      this.form = null;
      this.formUnchanged = null;
      this.showAmendment = false;
      return;
    }
    this.form = e as Form;
    if (!this.form) {
      this.showAmendment = false;
      return;
    }
    this.showAmendment = false;
    this.formUnchanged = e ? utils.cloneDeep(e) : null;
    this.createdOn = moment(this.form?.createdOn).format("MMM-DD-YYYY");
    this.setHeights();
    // this.scrollToTop('sidenav-content');
    // Keep the pending amendment visible so its owner can withdraw it before approval.
    if ((this.form.editingBy && this.form.formVersion.statusID == FormStatusEnum.AmendmentDrafting) ||
      [FormStatusEnum.AmendmentPending, FormStatusEnum.AmendmentReviewApprovals].includes(this.form.formVersion.statusID)) this.showAmendment = true;
  }

  /**
   * Determines whether a new amendment may be requested from the current EB workflow state.
   * @returns True when the amendment-submit action is available and unsigned.
   */
  showAmendmentButton() {
    const amendmentSignature = this.getAmendmentSignature();
    return this.form &&
      amendmentSignature?.amendmentEnabled &&
      !amendmentSignature.signed &&
      this.hasPrivilege(this.privilegeEnum.EBRequestAmendment) &&
      this.form.formVersion.statusID != FormStatusEnum.AmendmentDrafting &&
      this.form.formVersion.statusID != FormStatusEnum.AmendmentPending &&
      ![FormStatusEnum.AmendmentReviewApprovals,
        FormStatusEnum.AmendmentPendingRequesterConcurrence,
        FormStatusEnum.AmendmentPendingSafetyControls].includes(this.form.formVersion.statusID);
  }

  /**
   * Finds the current EB amendment request action without treating completed amendment
   * instances from earlier workflow steps as the active request action.
   * @returns The current coded action, or a legacy amendment-enabled action when the migration is pending.
   */
  private getAmendmentSignature() {
    const signatures = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(section => section.wfSignatures ?? []) ?? [];
    const currentAmendmentID = this.getActiveAmendmentID();
    const currentSignatures = signatures.filter(signature => (signature.wfAmendmentID ?? 0) == currentAmendmentID);
    return currentSignatures.find(signature => signature.code == this.amendmentSubmitCode && signature.amendmentEnabled && !signature.signed)
      ?? currentSignatures.find(signature => signature.code == this.amendmentSubmitCode && signature.amendmentEnabled)
      ?? currentSignatures.find(signature => signature.amendmentEnabled && !signature.signed)
      ?? currentSignatures.find(signature => signature.amendmentEnabled);
  }

  /**
   * Resolves the amendment identifier attached to the active workflow step.
   * @returns The active amendment identifier, or zero for the base workflow.
   */
  private getActiveAmendmentID(): number {
    const activeStep = this.form?.wfTable?.wfTableLocal?.wfSectionLocals
      .flatMap(section => section.wfStepLocals ?? [])
      .flatMap(step => step.wfSteps ?? [])
      .find(step => step.active);
    return activeStep?.wfAmmendmentID ?? 0;
  }

  formCreated(form: Form) {
    this.form = form;
    this.side.formCreated(this.form);
  }

  /**
   * Reloads the restored active version after an amendment is canceled and keeps the row selected.
   * @param cancelledForm Form instance captured before cancellation, when supplied by the main form.
   * If no persisted form exists, clears the selection used for a canceled new form.
   */
  async formCancelled(cancelledForm?: Form | null): Promise<void> {
    const cancelledFormID = cancelledForm?.id ?? this.form?.id;
    const cancelledAmendment = cancelledForm?.formVersion?.previousVersionID != null || this.form?.formVersion?.previousVersionID != null;
    this.showAmendment = false;

    if (cancelledFormID && cancelledAmendment) {
      try {
        const activeForm = await this.formService.getById(cancelledFormID).toPromise();
        if (activeForm) {
          // Replace the list row with the restored version so selection and amendment state stay synchronized.
          this.store.dispatch(new FormsUpdate(activeForm.id, activeForm));
          this.form = activeForm;
          this.side.form = activeForm;
          this.side.formTable.form = activeForm;
          this.rowSelected(activeForm);
          return;
        }
      }
      catch {
        this.alert.defaultError();
      }
    }

    this.form = null;
    this.side.form = null;
    this.side.formTable.form = undefined;
  }

  async duplicate() {
    if (await this.canDeactivate()) {
      let form = new Form(FormType.EBF);
      const user = this.getCurrentUser();
      const serialNo = await this.formService.getSerialNo(FormType.EBF).toPromise();
      if (serialNo && user && this.form) {
        // this.sidePanel.selectedRowIndex = -1;
        form = {
          id: 0,
          type: FormType.EBF,
          sequenceNumber: new SequenceNumber,
          formVersion: new FormVersion,
          editingById: user.id,
          editingBy: user,
          createdByUser: user,
          createdOn: new Date(),
          date: new Date()
        } as Form;
        form.sequenceNumber.number = serialNo?.serialNo;
        form.formVersion.statusID == FormStatusEnum.Draft;
        form.formVersion.operatorInitiatingForm = user;
        form.formVersion.operatorInitiatingFormID = user.id ?? 0;
        form.formVersion.requesterID = this.form.formVersion.requesterID;

        const eb2 = utils.cloneDeep(this.form.formVersion.eB2) as EB2;
        eb2.eB2AcceleratorLocations.map(x => { x.id == 0; x.eB2ID = 0; });
        eb2.eB2OtherSystems.map(x => { x.id = 0; x.eB2ID = 0; });
        eb2.eB2RelatedLinks.map(x => { x.formID = 0; x.id = 0; });
        eb2.eB2Restrictions.map(x => { x.id = 0; x.formID = 0; });
        eb2.eB2Shutters.map(x => { x.eB2ID = 0; x.id = 0; });
        eb2.id = 0;
        form.formVersion.id = 0;
        form.formVersion.eB2 = eb2;

        this.formService.create(FormHandler.toSave(form))?.subscribe(
          (data: Form) => {
            this.form = data;
            this.alert.message('DraftSaved', [
              new MessagePlaceholder("{serialNo}", data.sequenceNumber.number),
            ]);
            this.setOpenDocument(FormType.EBF, this.form.id);
            this.side.formCreated(this.form);
          },
          (error) => {
            console.error(error);
            this.alert.defaultError();
          }
        );

        this.disabled = false;
      }
    }
  }

  requestAmendment() {
    if (this.form?.wfTableID) {
      const confirm = this.dialog.open(YesNoDialogComponent, {
        width: "500px",
        data: {
          message: this.getMessage('request_amendment').description,
          icon: "warn",
        },
      });

      confirm.afterClosed().subscribe((data) => {
        if (data && this.form) {
          this.formVersionService.newVersion(this.form.formVersion.id).subscribe(async (formVersion: FormVersion) => {
            const amendmentSignature = this.getAmendmentSignature();
            if (this.form && formVersion && amendmentSignature) {
              this.form.formVersion = utils.cloneDeep(formVersion);
              await this.service.sign(this.form, amendmentSignature.taskID, amendmentSignature.amendmentID, undefined, '{editing:true}');
            }
          },
            (error) => {
              console.error(error);
              this.alert.defaultError();
              this.loading = false;
            }
          );
        }
      });
    }
  }

  async editAmendment() {
    if (this.form?.id) {
      await this.formService.setEditing(this.form.id, true).toPromise();
      this.showAmendment = true;
    }
  }

  async changeAmendmentToggle(e: MatSlideToggleChange) {
    // Is the user editing?
    if (this.form?.editingBy?.id && this.currentUser?.id && this.form?.editingBy?.id == this.currentUser?.id) {
      // Check if dirty and if dirty warn user
      if (await this.canDeactivate()) {
        // If not dirty or if user discards changes:
        this.showAmendment = e.checked
        return;
      }
      else {
        // Set the toggle back manually
        e.source.checked = this.showAmendment;
        return;
      }
    }
    this.showAmendment = e.checked;
    this.changeAmendmentToggle(e);
  }

  //////
  changedFilter() { }
  clearFilter() { }
  selectedFilter() { }
  createDisabled() { }
  editDisabled() { }
  action() { }

  applyFilter(e: any) {
    if (!e)
      this.valueFilter = '';
    this.filter = e;
  }

}
